import { memo } from 'react';
import PropTypes from 'prop-types';
import DrawerPanel from './DrawerPanel.jsx';

/** @type {Record<string, 'default'|'muted'|'purple'|'blue'|'green'|'amber'|'red'>} */
const VARIANT_TO_PANEL = {
  blue: 'blue',
  purple: 'purple',
  green: 'green',
  amber: 'amber',
  red: 'red',
  neutral: 'muted',
};

/** @type {Record<string, string>} */
const LABEL_COLOR = {
  blue: 'text-blue-400',
  purple: 'text-purple-400',
  green: 'text-emerald-400',
  amber: 'text-amber-400',
  red: 'text-red-400',
  neutral: 'text-content-muted',
};

/** @type {Record<string, { padding: 'none'|'sm'|'md'|'lg', rowGap: string, iconWrap: string, icon: string, value: string, stackY: string, desc: string, childrenWrap: string }>} */
const SIZE_STYLES = {
  md: {
    padding: 'md',
    rowGap: 'gap-3',
    iconWrap: 'mt-0.5 size-10',
    icon: 'size-5',
    value: 'text-base font-semibold leading-snug',
    stackY: 'space-y-1',
    desc: 'text-sm leading-relaxed text-content-muted',
    childrenWrap: 'pt-2 text-sm text-content-secondary',
  },
  sm: {
    padding: 'sm',
    rowGap: 'gap-2',
    iconWrap: 'mt-0 size-8',
    icon: 'size-4',
    value: 'text-sm font-semibold leading-snug',
    stackY: 'space-y-0.5',
    desc: 'text-xs leading-relaxed text-content-muted',
    childrenWrap: 'pt-1 text-xs text-content-secondary',
  },
};

/**
 * Compact entity / plane / source card: icon, uppercase label, primary value,
 * optional description; `children` for richer bodies (nested metadata, links).
 *
 * @param {string} props.label  Short category (e.g. “ORGANIZATION”, “Target Truth Plane”).
 * @param {import('react').ReactNode} props.value  Primary display value.
 * @param {import('react').ReactNode} [props.description]
 * @param {import('lucide-react').LucideIcon} [props.icon]
 * @param {'blue'|'purple'|'green'|'amber'|'red'|'neutral'} [props.variant='blue']
 * @param {'sm'|'md'} [props.size='md']  `sm` = dense grid (e.g. extracted entities); `md` = default padding and type scale.
 * @param {import('react').ReactNode} [props.children]
 * @param {string} [props.className]
 */
const DrawerEntityCard = ({
  label,
  value,
  description,
  icon: Icon,
  variant = 'blue',
  size = 'md',
  children,
  className = '',
}) => {
  const panelVariant = VARIANT_TO_PANEL[variant] ?? VARIANT_TO_PANEL.blue;
  const labelColor = LABEL_COLOR[variant] ?? LABEL_COLOR.blue;
  const s = SIZE_STYLES[size] ?? SIZE_STYLES.md;

  return (
    <DrawerPanel variant={panelVariant} padding={s.padding} className={className}>
      <div className={['flex', s.rowGap].join(' ')}>
        {Icon ? (
          <span
            className={[
              'inline-flex shrink-0 items-center justify-center rounded-lg border border-edge bg-surface/80',
              s.iconWrap,
            ].join(' ')}
          >
            <Icon className={[s.icon, labelColor].join(' ')} aria-hidden />
          </span>
        ) : null}
        <div className={['min-w-0 flex-1', s.stackY].join(' ')}>
          <p className={['text-[10px] font-semibold uppercase tracking-wider', labelColor].join(' ')}>
            {label}
          </p>
          <p className={[s.value, 'text-content'].join(' ')}>{value}</p>
          {description ? <div className={s.desc}>{description}</div> : null}
          {children ? <div className={s.childrenWrap}>{children}</div> : null}
        </div>
      </div>
    </DrawerPanel>
  );
};

DrawerEntityCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  description: PropTypes.node,
  icon: PropTypes.elementType,
  variant: PropTypes.oneOf(['blue', 'purple', 'green', 'amber', 'red', 'neutral']),
  size: PropTypes.oneOf(['sm', 'md']),
  children: PropTypes.node,
  className: PropTypes.string,
};

export default memo(DrawerEntityCard);
